import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api';
import { selectActiveOrg, useAuthStore } from '../auth/store';

// ---------------------------------------------------------------------------
// Failing fast
//
// The default query client retries twice with exponential backoff, so a dead
// API left a shimmering skeleton on screen for ten seconds before admitting
// anything was wrong — the worst possible answer, because it looks like slow
// data rather than no data. One quick retry absorbs a dropped connection;
// past that the server is down and the user deserves to be told now.
// ---------------------------------------------------------------------------

const FAIL_FAST = {
  retry: (failureCount: number, error: unknown) => {
    if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
    return failureCount < 1;
  },
  retryDelay: 300,
} as const;

/**
 * A server fault said in English, with the raw message kept as evidence rather
 * than as the headline. A 500 whose body reads "boom" must not be the sentence
 * a user is asked to understand.
 */
export function describeError(
  error: unknown,
  subject: string,
): { title: string; description: string; detail: string | null } {
  const status = error instanceof ApiError ? error.status : 0;
  const raw = error instanceof Error && error.message.trim() ? error.message.trim() : null;
  if (status === 401 || status === 403) {
    return {
      title: `You do not have access to ${subject}`,
      description:
        'Your session may have expired, or this organization is not yours — try signing in again or switching org.',
      detail: raw,
    };
  }
  if (status === 404) {
    return {
      title: `${subject[0].toUpperCase()}${subject.slice(1)} not found`,
      description: 'It may have been deleted, or it belongs to another organization.',
      detail: raw,
    };
  }
  if (status === 0) {
    return {
      title: `Could not reach the server`,
      description: `The request for ${subject} never got a reply. Check your connection, then try again.`,
      detail: raw,
    };
  }
  return {
    title: `Could not load ${subject}`,
    description: `The server failed while building this — nothing is wrong with your data. Try again, and if it keeps happening the API needs a look.`,
    detail: raw ? `${status} · ${raw}` : `HTTP ${status}`,
  };
}

// ---------------------------------------------------------------------------
// Types (mirror backend/src/modules/invoices responses)
// ---------------------------------------------------------------------------

export type InvoiceState = 'DRAFT' | 'OPEN' | 'PAID' | 'PARTIAL' | 'VOID';

/** How a logged contact left the building. Nothing in this app sends mail. */
export type ContactChannel = 'email' | 'copied' | 'manual';

/**
 * The one delivery that opened the invoice and started the payment clock —
 * written as the draft flips to OPEN. Null on drafts and on invoices migrated
 * in from Harvest, which has no delivery record to import.
 */
export interface SendRecord {
  /** ISO timestamp the invoice went out. */
  at: string;
  to: string | null;
  channel: ContactChannel | string;
  /** The subject line it went out under. */
  subject: string | null;
  note: string | null;
}

/**
 * Chase state — the collection loop's memory, on every invoice payload.
 * `neverChased` and `dueForFollowUp` are disjoint queues: a late invoice needs
 * either a FIRST chase or its NEXT one, never both. `needsChase` is their sum,
 * so the chips and the rows count the same invoices.
 */
export interface InvoiceChase {
  /** Chases logged. The original send is NOT one — it lives in `sent`. */
  count: number;
  /** ISO timestamp of the most recent chase (null = never chased). */
  lastSentAt: string | null;
  daysSince: number | null;
  /** YYYY-MM-DD the next chase is due (null once nothing is owed). */
  nextFollowUp: string | null;
  dueForFollowUp: boolean;
  neverChased: boolean;
  /** `neverChased || dueForFollowUp` — wants an email today. */
  needsChase: boolean;
  cadenceDays: number;
  /**
   * Chased `stopAfter` times already and still unpaid: OUT of the automatic
   * queue and waiting for a human. It is on the row, not only in the job, so
   * the screen and the scheduler can never count different queues.
   */
  paused: boolean;
  /** Chases the policy allows before pausing. 0 = never stop. */
  stopAfter: number;
  /**
   * A PERSON took this invoice off the automatic queue — the per-invoice
   * override of the org schedule.
   *
   * Deliberately NOT the same field as `paused`: that is the machine's own
   * stopping rule firing after N chases, this is somebody deciding this client
   * is handled another way. They leave the queue for opposite reasons and want
   * opposite remedies, so the screen spells them differently.
   *
   * A hold stops the SCHEDULER only — "Send reminder" stays live.
   */
  held: boolean;
  holdReason: string | null;
  holdAt: string | null;
  holdBy: string | null;
  sent: SendRecord | null;
  /**
   * ⚠ **THE MACHINE'S LAST GO AT THIS INVOICE, INCLUDING THE ONES THAT DID NOT
   * LAND.**
   *
   * A delivered automatic chase writes a reminder and appears in
   * `count`/`lastSentAt` like any other. A FAILED one writes nothing — which is
   * why it used to be invisible on the invoice while the queue behaved as
   * though it had been chased. `delivered` is `sent`/`filed` and nothing else.
   */
  autoAttempt: {
    day: string;
    at: string;
    outcome: string;
    to: string | null;
    reason: string | null;
    attempts: number;
    delivered: boolean;
    inFlight: boolean;
    failed: boolean;
    exhausted: boolean;
  } | null;
  /** The last automatic attempt reached nobody, and the money is still owed. */
  autoFailed: boolean;
}

export interface InvoiceReminder {
  id: string;
  /** ISO timestamp. */
  sentAt: string;
  channel: ContactChannel | string;
  to: string | null;
  subject: string | null;
  note: string | null;
  /**
   * The SCHEDULER sent this one, not a person.
   *
   * Automatic and manual chases share one table and one timeline deliberately —
   * an invoice has a single contact history, and "who did we email, when" must
   * not depend on which of two logs you opened. This flag is what keeps them
   * distinguishable, resolved on the server so no screen has to parse a note.
   */
  auto: boolean;
}

export interface InvoiceListItem {
  id: string;
  number: string;
  client: { id: string; name: string };
  /** The invoice's subject, falling back to the first line-item description. */
  subject?: string | null;
  /** True when `subject` is a stand-in taken from the first line item. */
  subjectFromLineItem?: boolean;
  /**
   * The recurring schedule this invoice appears to have come from, or null.
   * Derived on the server from the template's subject — see the warning in
   * `backend/src/modules/invoices/recurring-match.ts`; there is no
   * `Invoice.recurringInvoiceId` column yet.
   */
  recurring?: { templateId: string; templateSubject: string } | null;
  /** The client's purchase-order number, when they issue one. */
  poNumber?: string | null;
  lineCount?: number;
  issueDate: string;
  dueDate: string;
  state: InvoiceState;
  kind: string;
  currency: string;
  amount: number;
  taxAmount: number;
  paidAmount: number;
  paidDate: string | null;
  /** A draft scheduled to send itself: the instant (UTC ISO), or null. */
  scheduledSendAt?: string | null;
  /** Mirrored from Harvest while the sync owns it — cannot be edited or deleted here. */
  sourceOwned?: boolean;
  dueAmount: number;
  isLate: boolean;
  daysOverdue: number;
  chase: InvoiceChase;
}

export interface AgingBucket {
  amount: number;
  count: number;
}

export interface InvoiceSummary {
  outstanding: number;
  overdue: number;
  draftTotal: number;
  draftCount: number;
  /** Issue date of the oldest unsent draft — money nobody has asked for yet. */
  draftOldestIssueDate: string | null;
  draftOldestDays: number;
  openCount: number;
  lateCount: number;
  /** "7 late · 5 to chase today (3 never chased + 2 due to follow up)". */
  chase: {
    lateCount: number;
    /** neverChased + dueForFollowUp — the number the screen leads with. */
    needsChase: number;
    neverChased: number;
    dueForFollowUp: number;
    chased: number;
    cadenceDays: number;
    /** What the queue is worth — a row count says nothing about the money. */
    needsChaseAmount: number;
    /** Late, chased to the limit, waiting for a human rather than a 5th email. */
    paused: number;
    stopAfter: number;
  };
  aging: {
    current: AgingBucket;
    days1_30: AgingBucket;
    days31_60: AgingBucket;
    days61_90: AgingBucket;
    days90plus: AgingBucket;
  };
}

export interface InvoiceLineItem {
  id: string;
  description: string;
  agreement: { id: string; name: string } | null;
  quantity: number;
  unitPrice: number;
  amount: number;
  kind: string;
  /** Prepaid hours: what this line put in a bank, and whether the repair applies. */
  prepaid?: { bankedHours: number | null; canAdd: boolean };
}

export interface InvoicePayment {
  id: string;
  date: string;
  amount: number;
  method: string | null;
  notes: string | null;
}

/** The bill-to block printed on the invoice (edited on the client record). */
export interface ClientBilling {
  billingContactName: string | null;
  billingEmail: string | null;
  /**
   * EVERY OTHER ADDRESS this client's invoices go to — AP, their PM, a shared
   * billing@ list. They ride as CC on the same message, which is what ended the
   * "send from the app, then forward it by hand from Gmail" step.
   */
  billingEmailCc?: string[];
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  region: string | null;
  postalCode: string | null;
  country: string | null;
  taxId: string | null;
}

export interface InvoiceDetail extends Omit<InvoiceListItem, 'client'> {
  client: { id: string; name: string; notes: string | null } & ClientBilling;
  subtotal: number;
  /** Reduction taken off the subtotal before tax (0 when there is none). */
  discountAmount: number;
  notes: string | null;
  /** Explicit subject (null on invoices that never got one). */
  subject: string | null;
  poNumber: string | null;
  linkedTimeEntryCount: number;
  linkedExpenseCount: number;
  /** A draft that will send itself — when (UTC ISO), who set it, who gets a BCC. */
  scheduledSend: { at: string; by: string | null; copyTo: string | null } | null;
  /** What happened the last time a schedule fired on this invoice. */
  lastScheduledSend: {
    at: string;
    scheduledFor: string;
    outcome: 'sent' | 'filed' | 'refused' | 'failed' | 'missed';
    to: string | null;
    message: string;
  } | null;
  lineItems: InvoiceLineItem[];
  payments: InvoicePayment[];
  /** Every chase logged against this invoice, newest first (never the send). */
  reminders: InvoiceReminder[];
  /** The delivery that opened this invoice — who it went to and what it said. */
  sentRecord: (SendRecord & { id: string }) | null;
  /** The corrected invoice sent again, newest first — never chases. */
  resends: Array<{
    id: string;
    sentAt: string;
    channel: string;
    to: string | null;
    subject: string | null;
    note: string | null;
    sentBy: string | null;
  }>;
  /**
   * The rest of this client's outstanding book. Chasing #1156 while the same
   * client's #1144 sits late is two emails where one would do.
   */
  clientBalance: {
    others: Array<{
      id: string;
      number: string;
      dueDate: string;
      dueAmount: number;
      isLate: boolean;
      daysOverdue: number;
    }>;
    otherCount: number;
    otherDue: number;
    otherLateCount: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ClientOption {
  id: string;
  name: string;
}

export interface UnbilledEntry {
  id: string;
  /**
   * Hours this entry CONTRIBUTES to its line — not the hours it holds. The two
   * differ when a prepaid support block already paid for part of it.
   */
  date: string;
  hours: number;
  /** Hours of this entry a purchased support block already paid for. */
  prepaidHours: number;
  personName: string;
  taskName: string | null;
  notes: string | null;
}

/** An agreement that does not bill by the hour, and the hours tracked on it. */
export interface FeeWorkAgreement {
  name: string;
  basis: 'HOURLY' | 'MONTHLY_FEE' | 'FIXED_FEE' | 'NON_BILLABLE';
  /** "monthly fee" / "fixed fee" / "internal" — for a running sentence. */
  noun: string;
  hours: number;
}

/**
 * WORK THE SERVER REFUSED TO PRICE, and why.
 *
 * Both halves are money the app would otherwise invent, and both are printed
 * rather than silently dropped: work that happened must not disappear just
 * because it is not billable.
 */
export interface UnbilledExcluded {
  /** Support hours a prepaid block already paid for — billing them charges twice. */
  prepaid: { hours: number; amount: number; entryCount: number };
  /** Hours on retainer / fixed-bid agreements — cost, not an hours × rate line. */
  fee: { hours: number; entryCount: number; agreements: FeeWorkAgreement[] };
}

export interface UnbilledLine {
  source: 'time' | 'expense';
  agreementId: string | null;
  description: string;
  detail: string;
  kind: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  timeEntryIds: string[];
  expenseIds: string[];
  /** Time entries rolled into this line — reviewable/excludable before billing. */
  entries: UnbilledEntry[];
}

/**
 * How unbilled hours roll into lines. Harvest lets you choose before it builds
 * the draft; picking afterwards means deleting ten lines by hand.
 */
export type UnbilledGrouping = 'agreement' | 'task' | 'person' | 'entry';

export interface UnbilledPreview {
  client: ClientOption;
  from: string;
  to: string;
  groupBy: UnbilledGrouping;
  /** Lines the grouping could not merge because the bill rate differed. */
  rateSplits: number;
  lines: UnbilledLine[];
  excluded: UnbilledExcluded;
  totals: { hours: number; amount: number; entryCount: number; expenseCount: number };
}

/** One client's worth of unbilled work on the "Ready to bill" index. */
export interface UnbilledClientRow {
  clientId: string;
  clientName: string;
  /** Hours still OWED: tracked, less anything a prepaid block already paid for. */
  hours: number;
  amount: number;
  entryCount: number;
  expenseCount: number;
  oldestEntryDate: string | null;
  agreements: Array<{ name: string; amount: number }>;
  /** Uninvoiced support hours already paid for out of a purchased block. */
  prepaidHours: number;
  prepaidAmount: number;
  /** Tracked hours on agreements that bill a fee, not an hour. */
  feeHours: number;
  feeAgreements: FeeWorkAgreement[];
}

export interface UnbilledIndex {
  from: string | null;
  to: string;
  clients: UnbilledClientRow[];
  totals: {
    amount: number;
    hours: number;
    clientCount: number;
    entryCount: number;
    expenseCount: number;
    prepaidHours: number;
    prepaidAmount: number;
    feeHours: number;
  };
}

export interface LineItemInput {
  description: string;
  agreementId?: string | null;
  quantity: number;
  unitPrice: number;
  kind?: string;
  timeEntryIds?: string[];
  expenseIds?: string[];
}

export interface CreateInvoiceInput {
  clientId: string;
  issueDate?: string;
  dueDate?: string;
  kind?: string;
  notes?: string | null;
  subject?: string | null;
  poNumber?: string | null;
  discountAmount?: number;
  taxAmount?: number;
  lines: LineItemInput[];
}

export interface ListFilters {
  state?: string;
  clientId?: string;
  search?: string;
  from?: string;
  to?: string;
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useInvoices(filters: ListFilters) {
  return useQuery({
    queryKey: ['invoices', 'list', filters],
    queryFn: () =>
      api.get<InvoiceListItem[]>('/invoices', {
        params: {
          state: filters.state || undefined,
          clientId: filters.clientId || undefined,
          search: filters.search || undefined,
          from: filters.from || undefined,
          to: filters.to || undefined,
        },
      }),
    ...FAIL_FAST,
  });
}

export function useInvoiceSummary() {
  return useQuery({
    queryKey: ['invoices', 'summary'],
    queryFn: () => api.get<InvoiceSummary>('/invoices/summary'),
    ...FAIL_FAST,
  });
}

export function useInvoiceClients() {
  return useQuery({
    queryKey: ['invoices', 'clients'],
    queryFn: () => api.get<ClientOption[]>('/invoices/clients'),
    staleTime: 5 * 60_000,
  });
}

/**
 * "Who should I bill right now?" — every client with unbilled billable work,
 * biggest first. Defaults to everything unbilled through today.
 */
export function useUnbilled(range?: { from?: string; to?: string }) {
  const from = range?.from || undefined;
  const to = range?.to || undefined;
  return useQuery({
    queryKey: ['invoices', 'unbilled', { from, to }],
    queryFn: () => api.get<UnbilledIndex>('/invoices/unbilled', { params: { from, to } }),
    ...FAIL_FAST,
  });
}

/**
 * One definition of "fetch this invoice", so the detail page's `useQuery` and
 * the bulk chase queue's imperative `fetchQuery`/`prefetchQuery` share a cache
 * entry instead of each holding their own copy of the same invoice.
 */
export function invoiceDetailQuery(id: string) {
  return {
    queryKey: ['invoices', 'detail', id] as const,
    queryFn: () => api.get<InvoiceDetail>(`/invoices/${id}`),
  };
}

export function useInvoice(id: string | undefined) {
  return useQuery({
    ...invoiceDetailQuery(id ?? ''),
    enabled: Boolean(id),
    ...FAIL_FAST,
  });
}

// --- billed work (audit trail behind the line items) ------------------------

export interface BilledTimeEntry {
  id: string;
  date: string;
  personName: string;
  taskName: string | null;
  hours: number;
  rate: number;
  amount: number;
  notes: string | null;
  /**
   * ⚠ **SOMEBODY OTHER THAN THIS ENTRY'S OWNER WROTE THESE HOURS.**
   *
   * Present only for a foreign write, and built on the server from the
   * immutable `time_entry_events` trail — never from `updatedAt`, and never
   * from the row's last-writer stamp (which a later self-edit overwrites).
   * `summary` is the sentence recorded at the moment of the write.
   */
  editedBy: { name: string; at: string; summary: string } | null;
}

export interface BilledWork {
  agreements: Array<{
    agreementId: string;
    agreementName: string;
    hours: number;
    amount: number;
    entries: BilledTimeEntry[];
  }>;
  expenses: Array<{
    id: string;
    date: string;
    vendor: string | null;
    notes: string | null;
    amount: number;
    agreementName: string | null;
  }>;
  totals: {
    entryCount: number;
    expenseCount: number;
    hours: number;
    amount: number;
    /** How many billed entries carry somebody else's handwriting. */
    editedByOthers: number;
  };
}

// ---------------------------------------------------------------------------
// The client-facing document + the PDF file it is typeset into.
//
// `GET /invoices/:id/document` is the single source for everything an invoice
// says to a client: the print sheet renders this object, and the server
// typesets the very same object into `GET /invoices/:id/pdf`. Every string
// arrives pre-formatted so the sheet on screen and the file a client receives
// cannot drift apart.
// ---------------------------------------------------------------------------

export interface DocRow {
  label: string;
  value: string;
  strong?: boolean;
}

export interface DocumentLine {
  description: string;
  detail: string | null;
  quantity: string;
  unitPrice: string;
  amount: string;
}

export interface DocumentTotal {
  label: string;
  value: string;
  kind: 'plain' | 'total' | 'credit' | 'due';
}

export interface InvoiceDocument {
  invoiceId: string;
  number: string;
  /** "Invoice-1144-Halcyon-Travel-Co" — no extension. */
  fileName: string;
  currency: string;
  state: InvoiceState;
  isLate: boolean;
  lateNotice: string | null;
  watermark: string | null;
  company: {
    name: string;
    legalName: string | null;
    addressLines: string[];
    contactLine: string | null;
    website: string | null;
    taxId: string | null;
    logoUrl: string | null;
  };
  billTo: { name: string; lines: string[]; taxId: string | null; email: string | null };
  meta: DocRow[];
  subject: string | null;
  lines: DocumentLine[];
  totals: DocumentTotal[];
  /**
   * The one-click way to settle this invoice — null when the deployment cannot
   * take a card, or the invoice is a draft/void/settled. Rendered by
   * `InvoiceSheet`; the hosted page suppresses it because its own live button
   * sits above the sheet and one page does not need the same offer twice.
   */
  payOnline: {
    url: string;
    label: string;
    note: string;
    testNotice: string | null;
  } | null;
  remittance: {
    /** "How to pay", or "Prefer a bank transfer?" once a button sits above. */
    heading: string;
    payee: string;
    invoiceNumber: string;
    rows: Array<{ label: string | null; value: string }>;
    questionsEmail: string | null;
  };
  terms: { label: string; dueDate: string; extra: string | null };
  notes: { heading: string; body: string } | null;
  footer: string;
  appendix: unknown | null;
}

export function useInvoiceDocument(id: string | undefined) {
  return useQuery({
    queryKey: ['invoices', 'document', id],
    queryFn: () => api.get<InvoiceDocument>(`/invoices/${id}/document`),
    enabled: Boolean(id),
  });
}

export interface PdfOptions {
  /** Append the time & expense audit trail (Harvest's "detailed invoice"). */
  detail?: boolean;
}

/**
 * Fetches the server-rendered PDF as a blob. Goes through `fetch` rather than
 * the JSON api client because the response is a file — but it carries the same
 * Authorization + X-Organization headers, so org scoping is identical.
 */
async function fetchInvoicePdf(
  id: string,
  options: PdfOptions = {},
): Promise<{ blob: Blob; fileName: string }> {
  const state = useAuthStore.getState();
  const activeOrg = selectActiveOrg(state);
  const headers: Record<string, string> = { Accept: 'application/pdf' };
  if (state.accessToken) headers.Authorization = `Bearer ${state.accessToken}`;
  if (activeOrg) headers['X-Organization'] = activeOrg.id;

  const res = await fetch(`/api/invoices/${id}/pdf${options.detail ? '?detail=1' : ''}`, {
    headers,
  });
  if (!res.ok) {
    let message = `Could not build the PDF (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string };
      if (body?.message) message = body.message;
    } catch {
      /* non-JSON error body — keep the status message */
    }
    throw new Error(message);
  }
  // The server already named the file; honour it so the client receives
  // "Invoice-1144-Halcyon-Travel-Co.pdf" in every browser.
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  const plain = /filename="([^"]+)"/i.exec(disposition);
  const fileName = encoded
    ? decodeURIComponent(encoded[1])
    : plain
      ? plain[1]
      : `Invoice-${id}.pdf`;
  return { blob: await res.blob(), fileName };
}

/** Saves the PDF to the user's downloads and resolves with its filename. */
export async function downloadInvoicePdf(id: string, options: PdfOptions = {}): Promise<string> {
  const { blob, fileName } = await fetchInvoicePdf(id, options);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Safari needs the object URL to outlive the click.
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
  return fileName;
}

/**
 * A whole selection as ONE archive.
 *
 * ⚠ The old bulk action looped `downloadInvoicePdf`, which is N downloads —
 * and real Chrome raises an "Allow multiple downloads?" permission prompt at
 * the third file, so a fifteen-invoice month-end batch either half-completed
 * or required granting a permission that reads like malware. The server zips
 * it (`POST /invoices/pdf-archive`) and the browser saves one file.
 *
 * Resolves with what actually went in, so a partial archive can be SAID rather
 * than counted by the owner afterwards.
 */
export async function downloadInvoicePdfArchive(
  ids: string[],
  options: PdfOptions = {},
): Promise<{ fileName: string; included: number; skipped: number }> {
  const state = useAuthStore.getState();
  const activeOrg = selectActiveOrg(state);
  const headers: Record<string, string> = {
    Accept: 'application/zip',
    'Content-Type': 'application/json',
  };
  if (state.accessToken) headers.Authorization = `Bearer ${state.accessToken}`;
  if (activeOrg) headers['X-Organization'] = activeOrg.id;

  const res = await fetch('/api/invoices/pdf-archive', {
    method: 'POST',
    headers,
    body: JSON.stringify({ ids, detail: options.detail === true }),
  });
  if (!res.ok) {
    let message = `Could not build the archive (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string };
      if (body?.message) message = body.message;
    } catch {
      /* non-JSON error body — keep the status message */
    }
    throw new Error(message);
  }
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  const plain = /filename="([^"]+)"/i.exec(disposition);
  const fileName = encoded
    ? decodeURIComponent(encoded[1])
    : plain
      ? plain[1]
      : 'invoices.zip';

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);

  return {
    fileName,
    included: Number(res.headers.get('X-Archive-Included') ?? ids.length),
    skipped: Number(res.headers.get('X-Archive-Skipped') ?? 0),
  };
}

/** Loaded on demand — only when the user opens the "billed time" disclosure. */
export function useBilledWork(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['invoices', 'billed-work', id],
    queryFn: () => api.get<BilledWork>(`/invoices/${id}/billed-work`),
    enabled: Boolean(id) && enabled,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

function useInvalidateInvoices() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['invoices'] });
}

export function useUnbilledPreview() {
  return useMutation({
    mutationFn: (input: {
      clientId: string;
      from: string;
      to: string;
      groupBy?: UnbilledGrouping;
    }) => api.post<UnbilledPreview>('/invoices/unbilled-preview', input),
  });
}

export function useCreateInvoice() {
  const invalidate = useInvalidateInvoices();
  return useMutation({
    mutationFn: ({ fromUnbilled, ...input }: CreateInvoiceInput & { fromUnbilled?: boolean }) =>
      api.post<InvoiceDetail>(fromUnbilled ? '/invoices/from-unbilled' : '/invoices', input),
    onSuccess: invalidate,
  });
}

export interface InvoiceScheduleInput {
  clientId: string;
  agreementId: string;
  /** The agreed total — the server refuses rows that do not sum to it, to the cent. */
  total: number;
  /** Defaults to the agreement's name; each invoice adds "— Invoice i of N". */
  subject?: string;
  notes?: string | null;
  installments: Array<{ issueDate: string; dueDate?: string | null; amount: number }>;
}

/** A project billed in installments: N drafts, one transaction, all or none. */
export function useCreateInvoiceSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: InvoiceScheduleInput) =>
      api.post<{ invoices: Array<{ id: string; number: string }>; count: number; total: number; agreementId: string }>(
        '/invoices/schedule',
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      void queryClient.invalidateQueries({ queryKey: ['agreements'] });
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

/**
 * Issue the invoice — and say who it went to.
 *
 * The body is the delivery record. The server writes it in the same
 * transaction as the DRAFT→OPEN flip, so an open invoice can always answer
 * "we never received it" with an address, a date and a channel. Sending with
 * no record at all is deliberately not a thing this app can do.
 */
export function useSendInvoice() {
  const invalidate = useInvalidateInvoices();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: string;
      channel: ContactChannel;
      to?: string | null;
      subject?: string | null;
      note?: string | null;
      /**
       * The date the invoice is issued under (YYYY-MM-DD). A stale draft is
       * re-dated to today unless the sender says to keep its own date — and
       * the due date follows, so the terms the message quotes are the terms
       * the invoice carries.
       */
      issueDate?: string;
    }) => api.post<InvoiceDetail>(`/invoices/${id}/send`, input),
    onSuccess: invalidate,
  });
}

// ---------------------------------------------------------------------------
// Sending — for real
//
// `useSendInvoice` above records a delivery somebody else performed ("logged
// manually", "copied"). Everything below actually TRANSMITS: the server drafts
// the message, renders the PDF, mails it with the file attached and a hosted
// invoice link in it, and only then issues the draft or records the chase.
// ---------------------------------------------------------------------------

/** What this deployment can do with an outbound message, said on the button. */
export interface DeliveryStatus {
  /** The org's zone — the one a scheduled send's hour is read in. */
  timeZone?: string;
  mode: 'live' | 'file' | 'off';
  /** True only when a message actually leaves the building. */
  transmits: boolean;
  /** Development default: written to `fileDir/` and transmitted nowhere. */
  writesToDisk: boolean;
  from: string;
  fileDir: string;
  reason: string | null;
  hostedLinks: string;
  /**
   * ⚠ WHAT THE PAY BUTTON ON THE INVOICE YOU ARE ABOUT TO SEND WILL DO.
   *
   * `invoice-payment.ts` documents its `reason` as "printed beside the button /
   * in Settings" and it was printed in NEITHER: the only way to discover that
   * every invoice going out carried a TEST MODE pay button was to open the
   * client's own hosted page and look at it. It now rides here, beside the
   * EMAIL_MODE notice, under the same honesty rule.
   */
  payments?: {
    mode: 'live' | 'test' | 'off';
    live: boolean;
    test: boolean;
    reason: string | null;
    methods: string[];
    ach: boolean;
    configuredBy: string;
  };
}

export function useDeliveryStatus() {
  return useQuery({
    queryKey: ['invoices', 'delivery-status'],
    queryFn: () => api.get<DeliveryStatus>('/invoices/delivery'),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * One drafted message. **The server is the only drafter** — the composer,
 * "copy all as text" and a bulk send all read this, so what a human proof-reads
 * and what is transmitted are the same string.
 */
export interface InvoiceDraft {
  id: string;
  number: string;
  clientId: string;
  clientName: string;
  mode: 'send' | 'remind';
  /** The primary addressee — the client's `billingEmail`. */
  to: string | null;
  /** Every other address on the client record. Rendered as removable chips. */
  cc: string[];
  /**
   * The signed-in user's own address, resolved from the TOKEN. "Copy me" names
   * it; the browser never sends it back, so nothing on the wire can redirect a
   * copy of a client's invoice somewhere else.
   */
  copyTo: string | null;
  subject: string;
  body: string;
  currency: string;
  senderName: string;
  amount: number;
  dueAmount: number;
  dueDate: string;
  issueDate: string;
  isLate: boolean;
  daysOverdue: number;
  viewUrl: string | null;
  /** Hard stop: paid, void, or no billing email. Listed, never sent. */
  blocked: string | null;
  blockedCode: string | null;
  overridable: boolean;
  /**
   * Soft stop: **not owed a chase today** — either the money is not late yet
   * (`not-due`) or it was chased inside the cadence and the next follow-up is
   * still in the future (`chased-recently`). Excluded from a bulk chase unless
   * asked for.
   */
  caution: string | null;
  cautionCode: 'not-due' | 'chased-recently' | null;
  /** The one fact behind the exclusion, worded by the server. */
  cautionSummary: string | null;
}

export async function fetchInvoiceDrafts(
  ids: string[],
  options: { issueDate?: string | null; mode?: 'resend' | null } = {},
): Promise<InvoiceDraft[]> {
  const res = await api.post<{ drafts: InvoiceDraft[] }>('/invoices/drafts', {
    ids,
    issueDate: options.issueDate ?? null,
    ...(options.mode ? { mode: options.mode } : {}),
  });
  return res.drafts;
}

export function useInvoiceDraft(
  id: string | undefined,
  options: { enabled?: boolean; issueDate?: string | null; mode?: 'resend' | null } = {},
) {
  return useQuery({
    queryKey: ['invoices', 'draft', id, options.issueDate ?? null, options.mode ?? null],
    queryFn: async () =>
      (await fetchInvoiceDrafts([id as string], { issueDate: options.issueDate, mode: options.mode }))[0],
    enabled: Boolean(id) && (options.enabled ?? true),
    ...FAIL_FAST,
  });
}

export type DeliveryDisposition = 'sent' | 'filed' | 'refused' | 'failed' | 'skipped';

export interface DeliveryResult {
  id: string;
  number: string;
  clientName: string;
  mode: 'send' | 'remind' | 'resend';
  /** The whole audience as one line — "ap@acme.com, cc:pm@acme.com". */
  to: string | null;
  /** Every address the message reached, To then CC then BCC. */
  recipients: string[];
  disposition: DeliveryDisposition;
  reason: string | null;
  attachment: string | null;
  /** Addresses now remembered against the client, so the next send is addressed. */
  learned: string[];
  /** Other invoice numbers that rode on the SAME message (one client, one chase). */
  groupedWith: string[];
}

/** Send ONE invoice or reminder — PDF attached, hosted link included. */
export function useDeliverInvoice() {
  const invalidate = useInvalidateInvoices();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: string;
      /** One address or many. A single string still works. */
      to?: string | string[] | null;
      cc?: string[] | null;
      /** BCCs the signed-in user; their address comes from the token. */
      copyMe?: boolean;
      subject?: string | null;
      body?: string | null;
      note?: string | null;
      issueDate?: string | null;
      /** `resend` — the corrected invoice, sent again as an invoice. */
      mode?: 'resend';
    }) =>
      api.post<{ delivery: DeliveryResult; invoice: InvoiceDetail }>(
        `/invoices/${id}/deliver`,
        input,
      ),
    onSuccess: invalidate,
  });
}

/** Schedule a draft to send itself on a day, at an hour in the org's zone. */
export function useScheduleSend() {
  const invalidate = useInvalidateInvoices();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string; on: string; hour: number; copyMe: boolean }) =>
      api.post<{ scheduledSendAt: string; timeZone: string }>(`/invoices/${id}/schedule-send`, input),
    onSuccess: invalidate,
  });
}

/** Take a draft off its schedule. */
export function useCancelScheduledSend() {
  const invalidate = useInvalidateInvoices();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ scheduledSendAt: null }>(`/invoices/${id}/schedule-send`),
    onSuccess: invalidate,
  });
}

/**
 * Edit who this client's invoices go to, from the composer.
 *
 * The composer already SAVES what it sends, so this is the explicit half —
 * removing an address that has left the company. It writes only the recipient
 * columns; everything else about the client belongs to the client record.
 */
export function useSetRecipients() {
  const invalidate = useInvalidateInvoices();
  return useMutation({
    mutationFn: ({ id, to, cc }: { id: string; to: string[]; cc: string[] }) =>
      api.patch<InvoiceDetail>(`/invoices/${id}/recipients`, { to, cc }),
    onSuccess: invalidate,
  });
}

export interface BulkDeliveryReport {
  results: DeliveryResult[];
  sent: number;
  filed: number;
  skipped: number;
  failed: number;
  /** Messages actually put on the wire — fewer than invoices when grouped. */
  messages?: number;
  /** How many of them covered more than one invoice for one client. */
  grouped?: number;
  mode: 'live' | 'file' | 'off';
}

/**
 * Send a whole selection in ONE action. Chasing fifteen invoices was fifteen
 * modals and roughly ninety interactions; it is now a checkbox and a button.
 */
export function useDeliverMany() {
  const invalidate = useInvalidateInvoices();
  return useMutation({
    mutationFn: (input: {
      ids: string[];
      note?: string | null;
      includeNotDue?: boolean;
      copyMe?: boolean;
    }) => api.post<BulkDeliveryReport>('/invoices/deliver', input),
    onSuccess: invalidate,
  });
}

export function useVoidInvoice() {
  const invalidate = useInvalidateInvoices();
  return useMutation({
    mutationFn: (id: string) => api.post<InvoiceDetail>(`/invoices/${id}/void`),
    onSuccess: invalidate,
  });
}

export function useDeleteInvoice() {
  const invalidate = useInvalidateInvoices();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: boolean }>(`/invoices/${id}`),
    onSuccess: invalidate,
  });
}

/**
 * Delete a selection of drafts in one transaction. `confirmCount` is the number
 * typed into the confirmation dialog; the server refuses unless it equals the
 * number of ids, and deletes nothing unless every one is a draft.
 */
export function useDeleteInvoices() {
  const invalidate = useInvalidateInvoices();
  return useMutation({
    mutationFn: (input: { ids: string[]; confirmCount: number }) =>
      api.post<{ deleted: number; numbers: string[]; amount: number }>('/invoices/delete', input),
    onSuccess: invalidate,
  });
}

export function useUpdateInvoice() {
  const invalidate = useInvalidateInvoices();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & Partial<CreateInvoiceInput>) =>
      api.patch<InvoiceDetail>(`/invoices/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useRemovePayment() {
  const invalidate = useInvalidateInvoices();
  return useMutation({
    mutationFn: ({ id, paymentId }: { id: string; paymentId: string }) =>
      api.delete<InvoiceDetail>(`/invoices/${id}/payments/${paymentId}`),
    onSuccess: invalidate,
  });
}

/**
 * Log a chase. Nothing here transmits mail — the composer hands the draft to
 * the user's own mail client — so this is the record that a human did it, and
 * it is what the Late queue reads to answer "who do I chase this morning?".
 */
export function useLogReminder() {
  const invalidate = useInvalidateInvoices();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: string;
      channel: ContactChannel;
      to?: string | null;
      subject?: string | null;
      note?: string | null;
      sentAt?: string;
      /** `resend` — record the corrected invoice as sent again, not a chase. */
      kind?: 'resend';
    }) => api.post<InvoiceDetail>(`/invoices/${id}/reminders`, input),
    onSuccess: invalidate,
  });
}

/** Undo a mis-logged chase (wrong invoice, double-click, never actually sent). */
export function useRemoveReminder() {
  const invalidate = useInvalidateInvoices();
  return useMutation({
    mutationFn: ({ id, reminderId }: { id: string; reminderId: string }) =>
      api.delete<InvoiceDetail>(`/invoices/${id}/reminders/${reminderId}`),
    onSuccess: invalidate,
  });
}

/** A new draft with the same client, lines and terms — `POST /invoices/:id/duplicate`. */
export function useDuplicateInvoice() {
  const invalidate = useInvalidateInvoices();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<InvoiceDetail & { duplicatedFrom: string; soldHours: boolean }>(`/invoices/${id}/duplicate`),
    onSuccess: invalidate,
  });
}

export function useRecordPayment() {
  const invalidate = useInvalidateInvoices();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: string;
      amount: number;
      date: string;
      method?: string;
      notes?: string;
      /** Email the client a thank-you receipt once the payment has landed. */
      sendReceipt: boolean;
    }) =>
      api.post<InvoiceDetail & { receipt: { status: string; to: string | null } | null }>(
        `/invoices/${id}/payments`,
        input,
      ),
    onSuccess: invalidate,
  });
}

export interface BulkPaymentResult {
  recorded: number;
  amount: number;
  skipped: number;
  failed: number;
  results: Array<{
    id: string;
    number: string | null;
    client: string | null;
    outcome: 'recorded' | 'skipped' | 'failed';
    amount: number | null;
    reason: string | null;
    receipt: { status: string; to: string | null } | null;
  }>;
}

/** Pay a selection in full — one payment per invoice, the balance each was shown at. */
export function useRecordPayments() {
  const invalidate = useInvalidateInvoices();
  return useMutation({
    mutationFn: (input: {
      items: Array<{ id: string; expectedDue: number }>;
      date: string;
      method?: string;
      notes?: string;
      sendReceipts: boolean;
    }) => api.post<BulkPaymentResult>('/invoices/payments/bulk', input),
    onSuccess: invalidate,
  });
}

// ---------------------------------------------------------------------------
// Org company details (for the printable invoice header/footer).
// GET /api/settings/org is owned by the settings module and may not be
// deployed yet — this hook degrades to null and the print view falls back
// to the org name from the auth store.
// ---------------------------------------------------------------------------

export interface OrgCompanyDetails {
  name: string | null;
  /** Registered entity name, printed under the display name when different. */
  legalName: string | null;
  addressLines: string[];
  email: string | null;
  phone: string | null;
  website: string | null;
  /** Sender's VAT / EIN. */
  taxId: string | null;
  /** Logo image (URL or data: URI) printed above the company name. */
  logoUrl: string | null;
  /** Remittance block: bank / ACH / wire / cheque-to, one instruction per line. */
  paymentInstructions: string[];
  terms: string | null;
  /** Net payment terms in days, when the org stores them numerically. */
  termsDays: number | null;
  footer: string | null;
}

function firstString(obj: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const v = obj[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}

function firstNumber(obj: Record<string, unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const v = obj[key];
    const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

/** Accepts several plausible payload shapes; never throws. */
function normalizeOrgCompany(raw: unknown): OrgCompanyDetails | null {
  if (raw === null || typeof raw !== 'object') return null;
  const root = raw as Record<string, unknown>;
  // Company details may live at the root or under a nested key.
  const candidates = [root, root.company, root.settings, root.organization, root.org].filter(
    (o): o is Record<string, unknown> => o !== null && typeof o === 'object',
  );
  const merged: Record<string, unknown> = {};
  for (const c of [...candidates].reverse()) Object.assign(merged, c);

  const address = firstString(merged, ['address', 'companyAddress', 'billingAddress']);
  const addressLines: string[] = [];
  if (address) {
    addressLines.push(...address.split(/\r?\n/).map((l) => l.trim()).filter(Boolean));
  } else {
    const line1 = firstString(merged, ['addressLine1', 'address1', 'street']);
    const line2 = firstString(merged, ['addressLine2', 'address2']);
    const city = firstString(merged, ['city']);
    const state = firstString(merged, ['state', 'region']);
    const zip = firstString(merged, ['zip', 'postalCode', 'zipCode']);
    if (line1) addressLines.push(line1);
    if (line2) addressLines.push(line2);
    const cityLine = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    if (cityLine) addressLines.push(cityLine);
  }

  const displayName = firstString(merged, ['companyName', 'displayName', 'name']);
  const legalName = firstString(merged, ['legalName', 'registeredName']);
  const instructions = firstString(merged, [
    'paymentInstructions',
    'remitTo',
    'remittanceInstructions',
    'bankDetails',
  ]);

  return {
    name: displayName ?? legalName,
    // Only a second line when it genuinely differs from the printed name.
    legalName: legalName && legalName !== displayName ? legalName : null,
    addressLines,
    email: firstString(merged, ['billingEmail', 'email', 'contactEmail']),
    phone: firstString(merged, ['phone', 'phoneNumber']),
    website: firstString(merged, ['website', 'url']),
    taxId: firstString(merged, ['taxId', 'vatNumber', 'ein']),
    logoUrl: firstString(merged, ['logoUrl', 'logo', 'logoDataUri']),
    paymentInstructions: instructions
      ? instructions.split(/\r?\n/).map((l) => l.trim()).filter(Boolean)
      : [],
    terms: firstString(merged, ['invoiceTerms', 'paymentTerms', 'terms', 'defaultTerms']),
    termsDays: firstNumber(merged, ['paymentTermsDays', 'termsDays', 'netDays']),
    footer: firstString(merged, ['invoiceFooterNote', 'invoiceFooter', 'footer', 'invoiceNotes']),
  };
}

export function useOrgCompanyDetails() {
  return useQuery({
    queryKey: ['settings', 'org', 'company'],
    queryFn: async (): Promise<OrgCompanyDetails | null> => {
      try {
        const raw = await api.get<unknown>('/settings/org');
        return normalizeOrgCompany(raw);
      } catch {
        return null; // settings module absent or erroring — fall back gracefully
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}
